import { Component, DestroyRef, Inject, OnInit, Optional } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { MAT_BOTTOM_SHEET_DATA, MatBottomSheetRef } from "@angular/material/bottom-sheet";
import { MatIcon } from "@angular/material/icon";
import { AuthService } from "@core/auth.service";
import { IonicModule } from "@ionic/angular";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { GTMSegmentService } from "src/app/services/gtm-segment.service";
import { HomecardsService } from "@core/AuthServices/homeCards/homecards.service";

/** Typed shape of the deferred BeforeInstallPromptEvent passed via bottom-sheet data. */
interface DeferredInstallPrompt {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

interface AddHomeScreenData {
  deferredPrompt: DeferredInstallPrompt | null;
}

@Component({
  selector: "app-add-home-screen",
  templateUrl: "./add-home-screen.component.html",
  styleUrls: ["./add-home-screen.component.scss"],
  standalone: true,
  imports: [
    IonicModule,
    MatIcon,
    TranslatePipe,
  ],
})
export class AddHomeScreenComponent implements OnInit {
  public userId: string;
  constructor(
    private matRef: MatBottomSheetRef<AddHomeScreenComponent>,
    public translate: TranslateService,
    private authService: AuthService,
    private GTMSegment: GTMSegmentService,
    private homeCardsService: HomecardsService,
    private destroyRef: DestroyRef,
    @Optional() @Inject(MAT_BOTTOM_SHEET_DATA) private data: AddHomeScreenData | null
  ) {
    this.authService?.selectedlanguage$?.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(
      (l) => {
        if (l) {
          translate.use(l ? l : "en");
        }
      },
      (err) => {
        return err;
      }
    );
    this.GTMSegment.GTMPopupInitialization("AddHomeScreen");
  }

  async ngOnInit() {
    await this.authService
      ?.getUid("AddHomeScreenComponent")
      ?.then((uid) => {
        this.userId = uid;
      })
      .catch((error) => {
        return error;
      });
  }

  close() {
    this.matRef.dismiss();
  }

  addToHomeScreen() {
    // Prefer sheet data, then re-read the global BIP at click time — the event may
    // arrive after the sheet was opened with a null deferredPrompt.
    const deferredPrompt = this.resolveDeferredPrompt();

    if (deferredPrompt) {
      try {
        // prompt() must be called synchronously within the user gesture; invoking
        // it here (before any async boundary or dismiss animation) ensures the
        // browser's gesture context is still active.
        Promise.resolve(deferredPrompt.prompt())
          .then(() => deferredPrompt.userChoice)
          .then((choiceResult) => {
            if (choiceResult.outcome === "accepted") {
              // Return the promise so any rejection propagates to the chain's .catch().
              return this.onAddToHomeScreen();
            }
            return Promise.resolve();
          })
          .catch((_error) => {
            // Async rejection from prompt() or userChoice — silently handled.
          });
      } catch (_error) {
        // Synchronous throw from prompt() — silently handled so dismiss() always runs.
      }
    } else {
      // No native install prompt (desktop / BIP not fired yet). Still complete the
      // checklist step when the user explicitly taps Add, so they are not blocked.
      void this.onAddToHomeScreen();
    }

    // Dismiss the bottom sheet regardless of prompt availability, outcome, or errors.
    this.matRef.dismiss();
  }

  /** Sheet data first, then live window.customEvent from index.html BIP listener. */
  private resolveDeferredPrompt(): DeferredInstallPrompt | null {
    const fromData = this.data?.deferredPrompt ?? null;
    if (fromData) {
      return fromData;
    }
    try {
      return (typeof window !== "undefined" && window["customEvent"]) || null;
    } catch (_error) {
      return null;
    }
  }

  async onAddToHomeScreen(): Promise<void> {
    let userId = this.userId;
    // CTA can fire before ngOnInit getUid resolves — resolve on demand.
    if (!userId || userId === "null") {
      try {
        userId = await this.authService?.getUid("AddHomeScreenComponent");
        this.userId = userId;
      } catch (_error) {
        return;
      }
    }
    if (!userId || userId === "null") {
      return;
    }

    if (!navigator.onLine) {
      return;
    }
    this.homeCardsService.updateAddToHomeCardStatus({ event: "add_home_screen", status: true });
  }
}
